import { useRef, useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { taskEffort, taskCost, pctSum, fmtH, fmtC, locMap } from "@/lib/calc";
import { NumCell, TextCell } from "@/components/estimation/cells";
import { Upload, Sparkles, RotateCcw, Plus, Trash2, Loader2, Download, RefreshCw, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SHORT = { "Solution Architect": "SA", "ML/AI Engineer": "ML", "Backend Engineer": "BE", "Frontend Engineer": "FE", "Data Engineer": "DE", "QA Engineer": "QA", "Project Manager": "PM", "UX Designer": "UX" };
const shortRole = (n) => SHORT[n] || n.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 3);

export default function WbsTab({ est, setEst, oppId, reload }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [newWs, setNewWs] = useState("");
  const [prompt, setPrompt] = useState("");
  const [promptHistory, setPromptHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // { type: 'task'|'workstream', id?, name, ws?, count? }

  const loadPromptHistory = () => {
    api.get(`/opportunities/${oppId}/estimation/prompt-history`)
      .then((r) => setPromptHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  };

  useEffect(() => { loadPromptHistory(); }, [oppId]); // eslint-disable-line

  useEffect(() => {
    if (!job) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/opportunities/${oppId}/estimation/generate-status/${job}`);
        if (data.status === "done") {
          clearInterval(iv); setJob(null); setFile(null); setPrompt("");
          toast.success("AI estimation generated from BRD");
          loadPromptHistory();
          reload();
        } else if (data.status === "error") {
          clearInterval(iv); setJob(null);
          toast.error("Generation failed: " + (data.error || "unknown error"));
        }
      } catch (err) {
        // Job endpoint may briefly 404 right after creation — keep polling silently until done/error.
        console.debug("generate-status poll (transient)", err?.response?.status);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [job]); // eslint-disable-line

  const generate = async () => {
    if (!file) { fileRef.current?.click(); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (prompt.trim()) fd.append("prompt", prompt.trim());
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/generate`, fd);
      setJob(data.job_id);
      toast.info("Analyzing BRD with Claude Opus — this can take a couple of minutes…");
    } catch (e) { toast.error(apiError(e)); }
  };

  const regenerate = async () => {
    if (!window.confirm(
      "Re-run AI generation from the originally uploaded source?\n\nThis will REPLACE the current workstreams, tasks and resource load with a freshly generated WBS. A baseline snapshot of the new result will be saved automatically.")) return;
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/regenerate`,
                                       prompt.trim() ? { prompt: prompt.trim() } : {});
      setJob(data.job_id);
      toast.info(prompt.trim()
        ? "Re-running Claude Opus with your steering prompt — this can take a couple of minutes…"
        : "Re-running Claude Opus on the stored source — this can take a couple of minutes…");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reset = async () => {
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/reset`);
      setEst(data);
      toast.success("Estimation reset");
    } catch (e) { toast.error(apiError(e)); }
  };

  const patchTask = (tid, patch) => {
    setEst({ ...est, tasks: est.tasks.map((t) => (t.id === tid ? { ...t, ...patch } : t)) });
    api.put(`/opportunities/${oppId}/estimation/tasks/${tid}`, patch).catch((e) => { toast.error(apiError(e)); reload(); });
  };

  const addTask = async (wsName) => {
    try { const { data } = await api.post(`/opportunities/${oppId}/estimation/tasks`, { workstream: wsName }); setEst(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteTask = async (tid) => {
    try {
      const { data } = await api.delete(`/opportunities/${oppId}/estimation/tasks/${tid}`);
      setEst(data);
      toast.success("Task deleted — estimate and resource load updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteWorkstream = async (wsName) => {
    try {
      const { data } = await api.delete(`/opportunities/${oppId}/estimation/workstreams/${encodeURIComponent(wsName)}`);
      setEst(data);
      toast.success(`Workstream "${wsName}" deleted`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    if (confirmDel.type === "task") await deleteTask(confirmDel.id);
    else await deleteWorkstream(confirmDel.name);
    setConfirmDel(null);
  };

  const addWorkstream = async () => {
    if (!newWs.trim()) return;
    const ws = [...est.workstreams, { name: newWs.trim(), start_week: 1, end_week: est.num_weeks, contingency_pct: 0.1 }];
    try {
      const { data } = await api.put(`/opportunities/${oppId}/estimation/workstreams`, { workstreams: ws });
      setEst(data); setNewWs("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPct = (t, role, val) => {
    const role_pcts = { ...(t.role_pcts || {}) };
    if (val === null || val === 0) delete role_pcts[role]; else role_pcts[role] = val;
    patchTask(t.id, { role_pcts });
  };

  const roles = est.roles;
  const locs = locMap(est);
  const thCls = "bg-zinc-50 border-b border-zinc-300 border-r border-r-zinc-200 px-2 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium whitespace-nowrap";

  return (
    <div data-testid="wbs-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.xlsx" className="hidden" data-testid="brd-file-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button data-testid="brd-upload-button" onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 border border-zinc-300 hover:bg-zinc-50 text-sm px-3 py-2 rounded-sm">
          <Upload size={14} strokeWidth={1.5} /> {file ? file.name : "Choose document (BRD / RFP / PRD — PDF, DOCX, XLSX, TXT)"}
        </button>
        <button data-testid="generate-wbs-button" onClick={generate} disabled={!!job || !file}
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-50">
          {job ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
          {job ? "Generating estimate…" : "Generate WBS with AI"}
        </button>
        {est.brd_filename && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 font-mono">source: {est.brd_filename}</span>
            <button
              data-testid="download-brd-button"
              onClick={async () => {
                try {
                  const res = await api.get(`/opportunities/${oppId}/estimation/source`, { responseType: "blob" });
                  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = est.brd_filename;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  if (e?.response?.status === 404) {
                    toast.error("Source document is not available — it was uploaded before file storage was enabled. Re-upload to make it downloadable.");
                  } else {
                    toast.error(apiError(e));
                  }
                }
              }}
              title="Download original source document"
              className="flex items-center gap-1 text-xs text-[#002FA7] hover:bg-blue-50 px-2 py-1 rounded-sm">
              <Download size={12} strokeWidth={1.5} /> Download
            </button>
            <button
              data-testid="rerun-ai-button"
              onClick={regenerate}
              disabled={!!job}
              title="Re-run AI generation from the stored source document"
              className="flex items-center gap-1 text-xs text-[#002FA7] hover:bg-blue-50 px-2 py-1 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <RefreshCw size={12} strokeWidth={1.5} className={job ? "animate-spin" : ""} />
              {job ? "Re-running…" : "Re-run AI"}
            </button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <input data-testid="new-workstream-input" value={newWs} onChange={(e) => setNewWs(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addWorkstream()} placeholder="New workstream…"
              className="border border-zinc-300 rounded-sm px-2.5 py-1.5 text-xs w-44 outline-none focus:border-[#002FA7]" />
            <button data-testid="add-workstream-button" onClick={addWorkstream} className="border border-zinc-300 rounded-sm p-1.5 hover:bg-zinc-50"><Plus size={13} /></button>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="reset-estimation-button" className="flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-xs px-3 py-2 rounded-sm">
                <RotateCcw size={13} strokeWidth={1.5} /> Reset estimation
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading tracking-tight">Reset this estimation?</AlertDialogTitle>
                <AlertDialogDescription>All WBS tasks, workstreams and the resource load will be cleared. Rate card and pricing settings are kept. This action is audit-logged.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="reset-confirm" onClick={reset} className="bg-red-600 hover:bg-red-700">Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {(file || est.brd_filename) && !job && (
        <div className="flex items-center gap-2 mb-6 -mt-3">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">
            Steering prompt <span className="text-zinc-300 normal-case tracking-normal">(optional)</span>
          </label>
          <input
            data-testid="ai-prompt-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Lean offshore-first delivery; emphasize data engineering; assume 12-week timeline; include a discovery sprint…"
            maxLength={2000}
            className="flex-1 border border-zinc-300 rounded-sm px-3 py-1.5 text-xs outline-none focus:border-[#002FA7]" />
          {prompt && (
            <button
              data-testid="ai-prompt-clear"
              onClick={() => setPrompt("")}
              className="text-[11px] text-zinc-400 hover:text-zinc-700 px-2 py-1">clear</button>
          )}
          {promptHistory.length > 0 && (
            <div className="relative">
              <button
                data-testid="ai-prompt-history-button"
                onClick={() => setShowHistory((v) => !v)}
                className="flex items-center gap-1 text-[11px] border border-zinc-300 rounded-sm px-2 py-1.5 hover:bg-zinc-50 text-zinc-600 whitespace-nowrap">
                <History size={12} strokeWidth={1.5} /> Recent <ChevronDown size={11} strokeWidth={1.5} />
              </button>
              {showHistory && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowHistory(false)} />
                  <div data-testid="ai-prompt-history-menu"
                    className="absolute right-0 mt-1 w-96 max-w-[80vw] bg-white border border-zinc-200 rounded-sm shadow-lg z-20 overflow-hidden">
                    <p className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">Last {promptHistory.length} steering prompts</p>
                    {promptHistory.map((h, i) => (
                      <button
                        key={h}
                        data-testid={`ai-prompt-history-item-${i}`}
                        onClick={() => { setPrompt(h); setShowHistory(false); }}
                        className="block w-full text-left px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 truncate"
                        title={h}>
                        {h}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {est.workstreams.length === 0 ? (
        <div data-testid="wbs-empty-state" className="border border-dashed border-zinc-300 rounded-sm p-12 text-center">
          <p className="text-sm text-zinc-500 mb-1">No estimation yet.</p>
          <p className="text-xs text-zinc-400">Upload a BRD and generate the WBS with AI, or add a workstream manually.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={`${thCls} text-left min-w-[260px] sticky left-0 bg-zinc-50 z-10`}>Task / Deliverable</th>
                <th className={`${thCls} text-left min-w-[90px]`}>Epic</th>
                <th className={thCls}>Size</th>
                <th className={`${thCls} text-right`}>Base</th>
                <th className={`${thCls} text-right`}>Ovr</th>
                <th className={`${thCls} text-right`}>Eff hrs</th>
                {roles.map((r) => <th key={r.name} className={`${thCls} text-right`} title={r.name}>{shortRole(r.name)} %</th>)}
                <th className={`${thCls} text-right`}>Σ%</th>
                <th className={`${thCls} text-right`}>Cont %</th>
                <th className={`${thCls} text-right`}>Cost</th>
                <th className={thCls}></th>
              </tr>
            </thead>
            <tbody>
              {est.workstreams.map((ws) => {
                const tasks = est.tasks.filter((t) => t.workstream === ws.name);
                const wsHrs = tasks.reduce((a, t) => a + taskEffort(t, est.tshirt_sizes), 0);
                const wsCost = tasks.reduce((a, t) => a + taskCost(t, est.roles, est.tshirt_sizes, locs), 0);
                return [
                  <tr key={ws.name} className="bg-zinc-100/70">
                    <td colSpan={6 + roles.length} className="px-2 py-1.5 border-b border-zinc-200 sticky left-0">
                      <span className="text-xs font-medium tracking-tight">{ws.name}</span>
                      <span className="text-[11px] text-zinc-400 font-mono ml-3">{fmtH(wsHrs)} hrs · {fmtC(wsCost)}</span>
                      <span className="text-[10px] text-zinc-400 ml-2">· {tasks.length} task{tasks.length === 1 ? "" : "s"}</span>
                    </td>
                    <td colSpan={2} className="px-2 py-1.5 border-b border-zinc-200 text-right">
                      <button data-testid={`add-task-${ws.name}`} onClick={() => addTask(ws.name)}
                        className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline"><Plus size={11} /> task</button>
                    </td>
                    <td className="px-2 py-1.5 border-b border-zinc-200 text-center">
                      <button
                        data-testid={`delete-workstream-${ws.name}`}
                        onClick={(e) => { e.stopPropagation(); setConfirmDel({ type: "workstream", name: ws.name, count: tasks.length }); }}
                        title={`Delete workstream "${ws.name}"`}
                        className="text-zinc-400 hover:text-red-600 p-1 rounded-sm hover:bg-red-50">
                        <Trash2 size={13} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>,
                  ...tasks.map((t) => {
                    const sum = pctSum(t);
                    const ok = Math.abs(sum - 1) < 0.005 || sum === 0;
                    return (
                      <tr key={t.id} data-testid={`wbs-row-${t.id}`} className="hover:bg-zinc-50 group">
                        <td className="border-b border-r border-zinc-100 sticky left-0 bg-white group-hover:bg-zinc-50 min-w-[260px]">
                          <TextCell value={t.name} onCommit={(v) => patchTask(t.id, { name: v })} testId={`task-name-${t.id}`} className="px-2 py-1.5 text-[13px] font-sans" />
                        </td>
                        <td className="border-b border-r border-zinc-100"><TextCell value={t.epic} onCommit={(v) => patchTask(t.id, { epic: v })} className="px-2 py-1.5 text-xs" /></td>
                        <td className="border-b border-r border-zinc-100 px-1">
                          <select data-testid={`task-size-${t.id}`} value={t.size || ""} onChange={(e) => patchTask(t.id, { size: e.target.value || null })}
                            className="text-xs font-mono bg-transparent outline-none focus:ring-1 focus:ring-[#002FA7] rounded-[2px] px-1 py-1">
                            <option value="">—</option>
                            {Object.keys(est.tshirt_sizes).map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-[13px] text-zinc-400">{est.tshirt_sizes[t.size] || "—"}</td>
                        <td className="border-b border-r border-zinc-100 w-16"><NumCell value={t.override_hrs} onCommit={(v) => patchTask(t.id, { override_hrs: v })} testId={`task-override-${t.id}`} placeholder="—" /></td>
                        <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-[13px] font-medium">{fmtH(taskEffort(t, est.tshirt_sizes))}</td>
                        {roles.map((r) => (
                          <td key={r.name} className="border-b border-r border-zinc-100 w-14">
                            <NumCell value={(t.role_pcts || {})[r.name] || null} scale={100} placeholder="·"
                              onCommit={(v) => setPct(t, r.name, v)} testId={`task-pct-${t.id}-${shortRole(r.name)}`} />
                          </td>
                        ))}
                        <td className={`border-b border-r border-zinc-100 px-2 text-right font-mono text-xs ${ok ? "text-emerald-600" : "text-red-600 font-medium"}`}>
                          {sum === 0 ? "—" : Math.round(sum * 100) + "%"}
                        </td>
                        <td className="border-b border-r border-zinc-100 w-14"><NumCell value={t.contingency_pct} scale={100} onCommit={(v) => patchTask(t.id, { contingency_pct: v || 0 })} testId={`task-cont-${t.id}`} /></td>
                        <td className="border-b border-r border-zinc-100 px-2 text-right font-mono text-[13px]">{fmtC(taskCost(t, est.roles, est.tshirt_sizes, locs))}</td>
                        <td className="border-b border-zinc-100 px-1 text-center">
                          <button
                            data-testid={`delete-task-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); setConfirmDel({ type: "task", id: t.id, name: t.name, ws: t.workstream }); }}
                            title={`Delete task "${t.name || ""}"`}
                            className="text-zinc-400 hover:text-red-600 p-1.5 rounded-sm hover:bg-red-50">
                            <Trash2 size={13} strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    );
                  }),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">
              {confirmDel?.type === "workstream"
                ? `Delete workstream "${confirmDel?.name}"?`
                : `Delete this task?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel?.type === "workstream" ? (
                <>
                  This will remove the workstream and all <span className="font-medium">{confirmDel?.count ?? 0} task(s)</span> inside it.
                  The resource load will be rebuilt to reflect the change. Audit-logged.
                </>
              ) : (
                <>
                  <span className="font-medium">{confirmDel?.name || "(unnamed)"}</span> will be removed from <span className="font-medium">{confirmDel?.ws}</span>.
                  The estimate totals and resource load will update accordingly. Audit-logged.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
